/**
 * The composer / SEBS-frontier-ux experiment, resolved at the global (carvana-layout)
 * layer for Home + SRP + VDP, in the proxy for Home routing, and in Pages Router Home's
 * getServerSideProps — deliberately outside the per-page experiment aggregation so it
 * only buckets on the pages that opt in via isComposerExperimentPage.
 */
export const SEBS_COMPOSER_EXPERIMENT = 'car-merch-sebs-frontier-ux';

/**
 * Auto-open-keyboard experiment, bucketed server-side alongside SEBS_COMPOSER_EXPERIMENT and
 * handed to the Composer MFE through its `experiments` prop.
 */
export const SEBS_COMPOSER_KEYBOARD_EXPERIMENT =
  'car-merch-sebs-frontier-keyboard';

export const composerExperiments = {
  [SEBS_COMPOSER_EXPERIMENT]: {},
  [SEBS_COMPOSER_KEYBOARD_EXPERIMENT]: {}
};

export const composerExperimentNames = Object.keys(composerExperiments);

/**
 * Registers the composer experiments with the shared experiments list purely so the
 * proxy middleware's setExperimentCookies mirrors e.g. a ?car-merch-sebs-frontier-ux=<bucket>
 * query param into a cookie for QA/dev (see src/proxy-utils/setExperimentCookies.ts).
 * This does NOT re-enable the per-page bucketing aggregation described above — actual
 * bucketing still only happens in getShouldShowComposerTreatment.ts.
 */
export const clientSideBrowserCookieIdExperiments: Record<string, never> = {};

export const serverSideBrowserCookieIdExperiments = composerExperiments;

export const clientSideUserIdExperiments: Record<string, never> = {};

export const serverSideUserIdExperiments: Record<string, never> = {};

/**
 * Cookie or query param name that forces the composer experience on for QA/dev,
 * even when the real experiment bucket/device checks would otherwise say no.
 */
export const COMPOSER_ENABLED_OVERRIDE_PARAM = 'composer_enabled';

/**
 * Tells SRP to open the composer on land; the value is the entry point reported on the open
 * (e.g. composer_open=header-nav). Consumed once and stripped from the URL by useSyncUrl.
 */
export const COMPOSER_OPEN_PARAM = 'composer_open';

export const HEADER_NAV_ENTRY_POINT = 'header-nav';

/**
 * Closed set of reasons revertToControl can be called with, so a typo can't silently
 * corrupt the DataDog dimension used to diagnose why the safety mechanism fired.
 */
export type ComposerRevertReason =
  | 'component-error'
  | 'component-timeout'
  | 'ready-timeout';

/**
 * The experiment lives on Home (/), SRP (/cars), and the standard VDP (/vehicle/<id>) only.
 * VDP is matched by shape (a numeric vehicle ID, per getVehicleId.ts's parseInt-based
 * parsing) rather than by excluding known non-ID routes, so a future /vehicle/* route
 * doesn't silently become SEBS-eligible unless it's actually a numeric VDP URL.
 * x-pathname stays `/` when the proxy rewrites Home to /home-ar.
 */
const VDP_PATHNAME_REGEX = /^\/vehicle\/\d+\/?$/;

export const isComposerExperimentPage = (pathname: string): boolean => {
  return (
    pathname === '/' ||
    pathname.startsWith('/cars') ||
    VDP_PATHNAME_REGEX.test(pathname)
  );
};
